import type { BaseCabItemOption, BaseFarePolicyDetail, FareTerm } from '@/api';

/**
 * Interpolates policy terms with variables from the selected cab
 *
 * Replaces template variables (${variableName}) in policy text with actual values
 * from the selected cab's interpolation variables. Terms with missing variables
 * are filtered out, while terms with no variables are kept as-is.
 *
 * @param terms - Array of fare policy terms containing template variables
 * @param selectedCab - Selected cab option containing interpolation variables
 * @returns Array of fare terms with interpolated text values (filtered for available variables)
 *
 * Behavior:
 * - Terms with no interpolation variables: kept as-is
 * - Terms with all variables available: interpolated and kept
 * - Terms with missing variables: removed from result
 *
 */
export function getInterPolatedPolicyInfo(
	terms: BaseFarePolicyDetail['terms'],
	selectedCab: BaseCabItemOption,
): FareTerm[] {
	const interpolationVariables = selectedCab.interpolationVariables || {};

	return terms
		.filter((term) => {
			// Check if all interpolation variables in this term can be resolved
			const primaryVariables = extractVariableNames(term.text.primary);
			const secondaryVariables = extractVariableNames(term.text.secondary);
			const allVariables = [...primaryVariables, ...secondaryVariables];
			// If no variables found, keep the term as is
			if (!allVariables.length) {
				return true;
			}
			// Only keep term if all variables can be resolved
			return allVariables.every(
				(variable) => variable in interpolationVariables,
			);
		})
		.map((term) => ({
			...term,
			text: {
				primary: interpolateText(term.text.primary, interpolationVariables),
				secondary: interpolateText(term.text.secondary, interpolationVariables),
			},
		}));
}

export function getInterPolatedTermDetails(
	termDetails: BaseFarePolicyDetail['termDetails'],
	selectedCab: BaseCabItemOption,
): BaseFarePolicyDetail['termDetails'] {
	const interpolationVariables = selectedCab.interpolationVariables || {};
	return {
		...termDetails,
		tncs: termDetails.tncs
			.filter((tnc) => {
				const variables = extractVariableNames(tnc);
				return variables.every(
					(variable) => variable in interpolationVariables,
				);
			})
			.map((tnc) => interpolateText(tnc, interpolationVariables)),
	};
}

/**
 * Extracts all variable names from a text string
 *
 * @param text - Text containing template variables like ${variableName}
 * @returns Array of variable names found in the text
 */
function extractVariableNames(text: string): string[] {
	const matches = text.match(/\$\{([^}]+)\}/g);
	if (!matches) {
		return [];
	}

	return matches.map((match) => {
		const variableName = match.slice(2, -1); // Remove ${ and }
		return variableName.trim();
	});
}

/**
 * Interpolates a single text string with provided variables
 *
 * @param text - Text containing template variables like ${variableName}
 * @param variables - Object mapping variable names to their values
 * @returns Text with all template variables replaced
 */
function interpolateText(
	text: string,
	variables: Record<string, string>,
): string {
	return text.replace(/\$\{([^}]+)\}/g, (match, variableName) => {
		const trimmedVariableName = variableName.trim();
		return variables[trimmedVariableName] || match; // Keep original if no replacement found
	});
}
